import React from "react";
import { BaseCard, SVGIcon } from "../baseCard/baseCard.tsx";
import { getFontSizes, getTitleHeight, darkenColor } from "../baseCard/BaseCardUtils.ts";
import "./numberCard.css";

interface NumberCardItem {
  label: string;
  value: number;
}

interface NumberCardProps {
  category: string;
  title: string;
  minValue: number;
  maxValue: number;
  items: NumberCardItem[];
  highlight?: boolean;
}

const NumberCard: React.FC<NumberCardProps> = ({
  category,
  title,
  minValue,
  maxValue,
  items,
  highlight = false,
}) => {
  const fontSizes = getFontSizes(highlight);
  const titleHeight = getTitleHeight(highlight);
  const color = "var(--color-light-gray)";
  const shapeColor = darkenColor(color);

  const generateGridLines = () => {
    const lines = [];
    const gridCount = 6;
    
    for (let i = 0; i <= gridCount; i++) {
      const value = Math.round(minValue + (maxValue - minValue) * i / gridCount);
      const yPosition = `${(i / gridCount) * 100}%`;
      
      lines.push(
        <div key={i} className="numberCard-grid-line" style={{ bottom: yPosition }}>
          <span className="numberCard-grid-value">{value}</span>
          <div className="numberCard-grid-divider" />
        </div>
      );
    }
    
    return lines;
  };

  const renderBars = () => {
    return items.map((item, index) => {
      const percentage = Math.min(((item.value - minValue) / (maxValue - minValue)) * 100, 100);
      
      return (
        <div key={index} className="numberCard-bar-container">
          <div className="numberCard-bar-wrapper">
            <div className="numberCard-bar-background">
              <div 
                className="numberCard-bar-fill"
                style={{ height: `${percentage}%` }}
              />
              <span 
                className="numberCard-bar-value"
                style={{ 
                  bottom: `${percentage}%`,
                  fontSize: fontSizes.listItem
                }}
              >
                {item.value}
              </span>
            </div>
          </div>
        </div>
      );
    });
  };

  const svgPaths = {
    topShape: "M20.7637 1.11044e-05C9.71799 1.24175e-05 0.76368 8.95433 0.763673 20L0.763678 97.4131C9.37694 104.498 32.5399 112.037 67.7002 90.1162C103.94 67.5225 112.736 17.7127 114.172 0L20.7637 1.11044e-05Z",
    bottomShape: "M206.493 109.598C206.493 120.643 197.539 129.598 186.493 129.598H0.626953C8.79625 105.382 42.9206 56.9502 114.063 56.9502C185.206 56.9502 205.326 19.2817 206.493 0.447266V109.598Z"
  };

  return (
    <BaseCard highlight={highlight} color={color} className="numberCard-card">
      <SVGIcon
        className="baseCard-icon-top-right"
        viewBox="0 0 115 105"
        fillColor={shapeColor}
        path={svgPaths.topShape}
      />

      <SVGIcon
        className="baseCard-icon-bottom-left"
        viewBox="0 0 207 130"
        fillColor={shapeColor}
        path={svgPaths.bottomShape}
      />

      <div className="baseCard-content numberCard-content">
        <div className="numberCard-header">
          <div
            className="numberCard-category"
            style={{ fontSize: fontSizes.category }}
          >
            {category.toUpperCase()}
          </div>

          <div
            className="numberCard-title"
            style={{
              fontSize: fontSizes.title,
              height: titleHeight,
            }}
          >
            {title}
          </div>

          <div
            className="baseCard-divider numberCard-divider"
            style={{
              width: "calc(100% + 2 * var(--spacing-3))",
            }}
          ></div>
        </div>

        <div className="numberCard-chart">
          <div className="numberCard-grid">
            {generateGridLines()}
          </div>
          <div className="numberCard-bars">
            {renderBars()}
          </div>
        </div>
        
        <div className="numberCard-labels">
          {items.map((item, index) => (
            <span 
              key={index}
              className="numberCard-label"
              style={{ fontSize: fontSizes.listItem }}
            >
              {item.label}
            </span>
          ))}
        </div>
      </div>
    </BaseCard>
  );
};

export default NumberCard;